import { axios } from '@boot/axios.js';
import { isTokenValid, parseJWT } from '@utils/authUtils.js';
import ConnectionManager from '@src/connectionManager.js';
import { identify_user, posthog } from '@boot/posthog.js';
import { Sentry } from '@boot/sentry.js';
import { resetAllStores } from '@stores/index.js';
import { useSharedStore } from '@stores/shared.js';
import { i18n } from '@boot/i18n';
import { isDeprecatedDomain } from '@utils/tenantValidation.js';

let jwtRefresher = () => {}; // handle for setTimeout
const LOGIN_REQUEST_TIMEOUT_S = 5000; // 5 s

// Validation URLs for mobile app
const PROD_TENANT_VALIDATION_URL = 'https://api.iotorq-lean.com';
const STAGING_TENANT_VALIDATION_URL = 'https://api.staging.iotorq-lean.com';

export default {
    /**
     * Reset store state.
     */
    resetState() {
        // remove items from local storage
        this.removeJWT();
        this.removeUser();
        // reset pinia states
        this.$reset();
    },

    /**
     * Store JWT access & refresh tokens in Pinia and localStorage.
     * @param {Object} tokens - JWT token pair.
     * @param {string} tokens.access - Access token.
     * @param {string} tokens.refresh - Refresh token.
     */
    setJWT({ access, refresh }) {
        localStorage.setItem('token', JSON.stringify({ access, refresh }));
        this.token = { access, refresh };
    },

    /**
     * Remove JWT from local storage.
     */
    removeJWT() {
        localStorage.removeItem('token');
        this.token = null;
    },

    /**
     * Remove user from local storage.
     */
    removeUser() {
        localStorage.removeItem('user');
        this.user = null;
    },

    /**
     * Clear tenant in store.
     */
    clearTenant() {
        this.tenant = null;
        localStorage.removeItem('tenant');
    },

    /**
     * Clear tenant url in store.
     */
    clearTenantUrl() {
        this.tenantURL = null;
        localStorage.removeItem('tenantURL');
    },

    /**
     *
     * Set tenant in store and global axios.
     */
    setTenant(tenant) {
        this.tenant = tenant;
        localStorage.setItem('tenant', tenant);
    },

    /**
     * Set tenant url in store and global axios.
     */
    setTenantUrl(tenantUrl) {
        this.tenantURL = tenantUrl;
        localStorage.setItem('tenantURL', tenantUrl);
    },

    /**
     * Set code verifier in the store.
     * @param {String} codeVerifier - Code verifier.
     */
    setCodeVerifierAction(codeVerifier) {
        localStorage.setItem('codeVerifier', codeVerifier);
        this.codeVerifier = codeVerifier;
    },

    /**
     * Set provider type in the store.
     * @param {String} providerType - Provider type.
     */
    setProviderTypeAction(providerType) {
        localStorage.setItem('providerType', providerType);
        this.providerType = providerType;
    },

    /**
     * Clear code verifier in store.
     */
    clearCodeVerifier() {
        this.codeVerifier = null;
        localStorage.removeItem('codeVerifier');
    },

    /**
     * Clear provider type in store.
     */
    clearProviderType() {
        this.providerType = null;
        localStorage.removeItem('providerType');
    },

    // IMPORTANT: The following functions (hasValidAccessTokenAction, hasValidRefreshToken, isAuthenticated) must be a Vuex action (cannot be a getter)
    // as the value of the token will remain the same, causing the getter to return a cached value.
    // This is because the getter is only reactive to changes in the state. Hence, if the token itself
    // does not change, the getter will not re-evaluate the token validity via the isTokenValid function.
    // See https://vuex.vuejs.org/guide/getters.html#property-style-access for more information.

    /**
     * Determine whether access token is valid.
     * Note: See IMPORTANT note above.
     * @returns {Boolean} Whether the access token is valid.
     */
    hasValidAccessTokenAction() {
        if (this.token) {
            return isTokenValid(parseJWT(this.token['access']));
        }
        return false;
    },

    /**
     * Determine whether refresh token is valid.
     * Note: See IMPORTANT note above.
     * @returns {Boolean}
     */
    hasValidRefreshTokenAction() {
        if (this.token) {
            return isTokenValid(parseJWT(this.token['refresh']));
        }
        return false;
    },

    /**
     * Determine whether the user is authenticated.
     * Note: See IMPORTANT note above.
     * @returns {boolean}
     */
    async isAuthenticatedAction() {
        const hasValidRefresh = await this.hasValidRefreshTokenAction();
        return this.user !== null && hasValidRefresh;
    },

    /**
     * Get a new access token from the server.
     */
    async refreshJWT() {
        console.debug('Automatically Refresh JWT');

        // Check if the refresh token is valid
        const hasValidRefresh = await this.hasValidRefreshTokenAction();
        if (!hasValidRefresh) {
            clearTimeout(jwtRefresher);
            return Promise.reject(new Error('Invalid refresh token'));
        }

        try {
            const response = await axios.post('auth/jwt/refresh/', {
                refresh: this.getRefreshToken
            });

            // save the new tokens
            this.updateAccessTokenAction(response.data['access']);

            // If the refresh token is returned, update the store (Added in v1.49.2) for indefinite sessions
            // and refresh the push notification registration expiry (only for Capacitor) by registering the device again.
            if (response.data['refresh']) {
                this.updateRefreshTokenAction(response.data['refresh']);

                if (process.env.MODE === 'capacitor') {
                    // TODO: Check if push notification manager is available
                    this.$pushNotificationManager.registerDevice();
                }
            }
        } catch (error) {
            console.error(
                `Failed to refresh access token due to ${JSON.stringify(error)}`
            );
        } finally {
            this.autoRefreshJWTAction();
        }
    },

    /**
     * Converts milliseconds to a human-readable time format without milliseconds.
     * @param {number} duration - Time in milliseconds.
     * @returns {string} - Time in human-readable format (e.g., "1h 2m 3s").
     */
    msToTime(duration) {
        const seconds = Math.floor((duration / 1000) % 60);
        const minutes = Math.floor((duration / (1000 * 60)) % 60);
        const hours = Math.floor(duration / (1000 * 60 * 60));

        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (seconds > 0) parts.push(`${seconds}s`);

        return parts.join(' ') || '0s';
    },

    /**
     * Decodes a JWT and calculates the time until it expires.
     * @param {string} token - The JWT token.
     * @returns {number} - Time in milliseconds until the token expires.
     */
    getTokenExpiryTimeMs(token) {
        // If token is valid, parse the JWT and calculate the time until it expires.
        if (isTokenValid(parseJWT(token))) {
            const decodedJWT = parseJWT(token);
            const msNow = Date.now();
            const msExpiry = decodedJWT['exp'] * 1000;
            return msExpiry - msNow; // Time until token expiry in milliseconds
        }

        // If token is invalid, return 0.
        return 0;
    },

    /**
     * Handles refreshing the access token before it expires.
     */
    autoRefreshJWTAction() {
        // Get access and refresh tokens from the store
        const accessToken = this.getAccessToken;
        const refreshToken = this.getRefreshToken;

        // Calculate and log expiry times for both tokens
        const msUntilAccessExpiryMs = this.getTokenExpiryTimeMs(accessToken);
        const msUntilRefreshExpiryMs = this.getTokenExpiryTimeMs(refreshToken);

        // Print expiry times for debugging
        console.debug(
            `Access token expires in: ${this.msToTime(msUntilAccessExpiryMs)}`
        );
        console.debug(
            `Refresh token expires in: ${this.msToTime(msUntilRefreshExpiryMs)}`
        );

        // Refresh the access token if it's still valid
        if (isTokenValid(parseJWT(accessToken)) && msUntilAccessExpiryMs > 0) {
            const slack = 5 * 60000; // 5 minutes slack to refresh before actual expiry
            const msRefreshDelayMs = Math.max(
                msUntilAccessExpiryMs - slack,
                15000
            ); // Minimum delay of 15 seconds

            console.debug(
                `Access token refreshes in: ${this.msToTime(msRefreshDelayMs)}`
            );

            // Schedule the next token refresh
            jwtRefresher = setTimeout(async () => {
                try {
                    await this.refreshJWT();
                } catch (error) {
                    console.error('Error while refreshing JWT:', error);
                }
            }, msRefreshDelayMs);
        }
    },

    /**
     * Authenticate a user by confirming username + password.
     * @param {Object} auth - Authentication fields.
     * @param {string} auth.username - Username.
     * @param {string} auth.password - Password.
     * @returns {Promise}
     */
    async authenticateAction({ username, password }) {
        // Unauthenticated views needs to send a CSRF token
        // Currently, we refresh the CSRF token every time we need it
        // As logging in the admin portal will invalidate the old token
        const csrfToken = await this.getCsrfTokenAction();

        // Check if CSRF token is available
        if (!csrfToken) {
            return Promise.reject('CSRF token is not available.');
        }

        return new Promise((resolve, reject) => {
            axios
                .post(
                    '/auth/login/',
                    {
                        username: username,
                        password: password
                    },
                    {
                        headers: {
                            'X-CSRFToken': csrfToken
                        },
                        timeout: LOGIN_REQUEST_TIMEOUT_S,
                        disableRequestInterceptor: true // Disable request interceptor on unauthenticated requests
                    }
                )
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Validate one time password.
     * @param {Object} otp - one-time password and ephemeral token.
     * @param {string} otp.otp - One-time password.
     * @param {string} otp.ephemeralToken - Ephemeral token.
     * @returns {Promise}
     */
    async validateOtpAction({ otp, ephemeralToken }) {
        // Unauthenticated views needs to send a CSRF token
        // Currently, we refresh the CSRF token every time we need it
        // As logging in the admin portal will invalidate the old token
        const csrfToken = await this.getCsrfTokenAction();

        // Check if CSRF token is available
        if (!csrfToken) {
            return Promise.reject('CSRF token is not available.');
        }

        return new Promise((resolve, reject) => {
            axios
                .post(
                    'auth/login/code/',
                    {
                        code: otp,
                        ephemeral_token: ephemeralToken
                    },
                    {
                        headers: {
                            'X-CSRFToken': csrfToken
                        },
                        timeout: LOGIN_REQUEST_TIMEOUT_S,
                        disableRequestInterceptor: true // Disable request interceptor on unauthenticated requests
                    }
                )
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Set JWT in local storage.
     * @param {Object} tokens - Object containing access and refresh tokens.
     * @param {string} tokens.access - Access token.
     * @param {string} tokens.refresh - Refresh token.
     */
    setJWTAction({ access, refresh }) {
        this.setJWT({ access, refresh });
        // as this application can have very long sessions, we need
        // a way to send information about who is the current user
        // to Sentry and Posthog. This is done by setting the user
        // each time we set the JWT. As Posthog sessions are limited
        // to 30 minutes, this allow us to keep track of the user even if
        // the user account was loaded several hours ago.
        const user = this.user;
        if (user) {
            try {
                console.debug('Setting up user with Sentry and Posthog');
                // inform Sentry of who is the current user
                Sentry.setUser(user);
                // identify current user in Posthog
                identify_user(user);
            } catch (error) {
                console.warning(
                    `Failed to set user with Sentry and Posthog ${JSON.stringify(
                        error
                    )}`
                );
            }
        }
    },

    /**
     * Handle user logout.
     */
    async handleLogout() {
        let tokenToBlacklist = null;
        const hasValidRefreshToken = await this.hasValidRefreshTokenAction();

        // Identify the token to blacklist before resetting the store
        if (hasValidRefreshToken) {
            tokenToBlacklist = this.getRefreshToken;
        }

        // reset Sentry current user
        Sentry.getCurrentScope().setUser(null);

        // reset all stores from pinia
        resetAllStores();

        // remove authorization headers from axios instance
        delete axios.defaults.headers['Authorization'];

        // remove signalR handlers
        ConnectionManager.removeHandler('startPhase');
        ConnectionManager.removeHandler('notifyDowntime');
        ConnectionManager.removeHandler('resolveNotification');
        ConnectionManager.removeHandler('newStreamEvent');
        ConnectionManager.removeHandler('newFacilityEvent');
        await ConnectionManager.destroy();

        // stop JWT refresher
        console.debug('Clearing JWT refresher');
        clearTimeout(jwtRefresher);

        // we need to blacklist the refresh token if it is still valid
        if (tokenToBlacklist) {
            await this.blacklistToken(tokenToBlacklist);
        }

        // Reset posthog when it is not running, it will log an error - line 219.
        if (process.env.POSTHOG_PROJECT_API_KEY) {
            // clear posthog data
            posthog.reset();
        }
    },

    /**
     * Send a request to add the current token to blacklist
     * @param {string} refreshToken - The refresh token to add to blacklist
     * @returns {Promise<void>} - The promise that resolves when the token is added to blacklist
     */
    async blacklistToken(refreshToken) {
        try {
            const response = await axios.post(
                'auth/token/blacklist/',
                {
                    refresh: refreshToken
                },
                {
                    disableRequestInterceptor: true // Disable request interceptor on unauthenticated requests
                }
            );
            return response;
        } catch (error) {
            console.error('Blacklist token failed due to', error);
        }
    },

    /**
     * Logs out user from all tabs/windows.
     */
    logoutAction() {
        // logout from all tabs/windows (triggers the localstorage listener on logout)
        localStorage.setItem('logout', Date.now());

        // Perform certain logout steps before redirecting
        this.handleLogout();
    },

    /**
     * Obtain the user's details from the server and load it in the store.
     * @returns {Promise<void>} - The promise that resolves when the user is loaded
     */
    async loadCurrentUserAction() {
        return new Promise((resolve, reject) => {
            // get user details
            axios
                .get('users/me/')
                .then(response => {
                    // set user in store
                    const user = response.data;
                    this.setUserAction(user);
                    // set user in Sentry and Posthog
                    try {
                        Sentry.setUser(user);
                        identify_user(user);
                    } catch (error) {
                        console.warning(
                            `Could not register user in Sentry or Posthog ${JSON.stringify(
                                error
                            )}`
                        );
                    }
                    resolve(response);
                })
                .catch(error => {
                    console.error('Failed to load user', error);
                    reject(error);
                });
        });
    },

    /**
     * Updates user details.
     * @param {Object} user
     * @param {number} user.id - User's id.
     * @param {String} user.username - User's username
     * @param {string} user.email - User's email.
     * @param {string} user.first_name - User's first name.
     * @param {string} user.last_name - User's last name.
     * @param {string} user.job_title - User's job title.
     * @param {string} user.role - User's role.
     * @param {string} user.language - User's language.
     * @param {Object[]} user.accesses - User's accesses.
     * @returns {Promise}
     */
    updateCurrentUserAction(user) {
        const sharedStore = useSharedStore();
        // begin loading
        sharedStore.beginLoading(`${i18n.global.t('Updating user')}...`);
        return new Promise((resolve, reject) => {
            // update user in database
            axios
                .put(`users/${user.id}/`, user)
                .then(response => {
                    const updatedUser = response.data;
                    // update user in store
                    this.setUserAction(updatedUser);
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
                .finally(() => {
                    // end loading
                    sharedStore.stopLoading();
                });
        });
    },

    /**
     * Updates access token.
     * @param {String} accessToken - New access token.
     * @returns {Promise} - Promise that resolves when the access token is updated.
     */
    updateAccessTokenAction(accessToken) {
        this.setJWT({
            access: accessToken,
            refresh: this.getRefreshToken
        });
    },

    /**
     * Updates refresh token.
     * @param {String} refreshToken - New refresh token.
     * @returns {Promise} - Promise that resolves when the refresh token is updated.
     */
    updateRefreshTokenAction(refreshToken) {
        this.setJWT({
            access: this.getAccessToken,
            refresh: refreshToken
        });
    },

    /**
     * Handle tenant verification.
     * @param {string} tenant - Tenant to verify.
     * @returns {Promise<boolean>} - True if tenant is valid, false otherwise.
     */
    async verifyAndSetTenantAction(tenant) {
        const baseVerificationURL = this.getTenantBaseVerificationURL(tenant);
        const verifyTenantUrl = `${baseVerificationURL}/api/clients/verify/?subdomain=${tenant}`;
        try {
            const response = await axios.get(
                verifyTenantUrl,
                {
                    disableRequestInterceptor: true // Disable request interceptor on unauthenticated requests
                },
                { timeout: LOGIN_REQUEST_TIMEOUT_S }
            );

            if ('is_public' in response.data && response.data.is_public) {
                return false;
            }

            // set tenant in store
            // this will also set the baseURL for axios and save it in localStorage
            this.setTenantAction(tenant);
            return true;
        } catch (error) {
            this.clearTenantAction();
            if (error.response && error.response.status === 404) {
                return false;
            } else {
                throw error;
            }
        }
    },

    /**
     * Sets tenant in store and global axios.
     * Set Axios base URL to tenant's URL.
     * @param {string} tenant
     */
    setTenantAction(tenant) {
        const tenantURL = `https://${tenant}.iotorq-lean.com/v1.1/api/`;

        // Validate against deprecated domains before setting
        if (isDeprecatedDomain(tenantURL)) {
            console.error(
                'Attempted to set deprecated domain as tenant:',
                tenantURL
            );
            throw new Error('Cannot set deprecated domain as tenant URL');
        }

        axios.defaults.baseURL = tenantURL;
        this.setTenant(tenant);
        this.setTenantUrl(tenantURL);
    },

    /**
     * Clear tenant in store.
     * Clear Axios base URL.
     */
    clearTenantAction() {
        axios.defaults.baseURL = null;
        this.clearTenant();
        this.clearTenantUrl();
    },

    /**
     * Get CSRF token from server.
     * @returns {Promise<string|null>} - Resolves with the CSRF token if available, null if not.
     */
    async getCsrfTokenAction() {
        let csrfToken = null;
        await axios
            .get('csrf/', {
                disableRequestInterceptor: true // Disable request interceptor on unauthenticated requests
            })
            .then(response => {
                csrfToken = response.data['csrfToken'];
            })
            .catch(error => {
                console.error('Failed to get CSRF token due to: ', error);
            });

        return csrfToken;
    },

    /**
     * Reset password.
     * @param {string} email - Email address of the user.
     * @returns {Promise<void>} - Resolves when the password reset request is sent.
     */
    async resetPasswordAction(email) {
        // Unauthenticated views needs to send a CSRF token
        // Currently, we refresh the CSRF token every time we need it
        // As logging in the admin portal will invalidate the old token
        const csrfToken = await this.getCsrfTokenAction();

        // Check if CSRF token is available
        if (!csrfToken) {
            return Promise.reject('CSRF token is not available.');
        }

        return new Promise((resolve, reject) => {
            axios
                .post(
                    'users/reset_password/',
                    { email: email },
                    {
                        headers: {
                            'X-CSRFToken': csrfToken
                        },
                        disableRequestInterceptor: true // Disable request interceptor on unauthenticated requests
                    }
                )
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Activate user account.
     * @param {Object} payload
     * @param {Number} payload.uid
     * @param {String} payload.token
     * @param {String} payload.new_password
     * @returns {Promise} - Promise that resolves when the user is activated.
     */
    async activateUserAction(payload) {
        // Unauthenticated views needs to send a CSRF token
        // Currently, we refresh the CSRF token every time we need it
        // As logging in the admin portal will invalidate the old token
        const csrfToken = await this.getCsrfTokenAction();

        // Check if CSRF token is available
        if (!csrfToken) {
            return Promise.reject('CSRF token is not available.');
        }

        return new Promise((resolve, reject) => {
            axios
                .post('users/activation/', payload, {
                    headers: {
                        'X-CSRFToken': csrfToken
                    },
                    disableRequestInterceptor: true // Disable request interceptor on unauthenticated requests
                })
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Get Tenant Validation URL
     * @param {String} tenant - Tenant account name.
     * @returns {String} - Tenant Validation URL
     */
    getTenantBaseVerificationURL(tenant) {
        // check if subdomain belongs to staging
        // dev = main tenant for staging environment
        if (tenant.toString().toLowerCase() === 'dev') {
            return STAGING_TENANT_VALIDATION_URL;
        }

        return PROD_TENANT_VALIDATION_URL;
    },

    /**
     * Set user in the store and local storage.
     * @param {Object} user - User object.
     */
    setUserAction(user) {
        localStorage.setItem('user', JSON.stringify(user));
        this.user = { ...user };
    },

    /**
     * Update user in the store.
     * @param {Object} user - User object.
     */
    updateUserAction(user) {
        this.user = { ...user };
    }
};
